import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {DepotState} from 'appredux/reducers/reducer-depots';
import {CSVLink} from 'react-csv';
import {Box, Button, CircularProgress} from '@material-ui/core';
import {
  IFarmInputProductDepotInfo,
  ISupplierUserProfile,
} from 'constants/interfaces';
import moment from 'moment';
import {ProductState} from 'appredux/reducers/reducer-products';
import {apiGetProductDepotInfo, apiSaveProductDepotInfo} from 'constants/Api';
import Papa from 'papaparse';
import {
  generateInventoryCsv,
  InventoryCsvMapper,
  InventoryCsvRow,
} from 'screens/Dashboard/Inventory/InventoryCsvUtils';
import {Alert, AlertTitle} from '@material-ui/lab';
import {reduxUpdateProductDepotInfo} from 'appredux/actions';
import {PermissionsService} from 'services/PermissionsService';

function InventoryCsvUploadDialog(props: {
  open: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
  user: ISupplierUserProfile;
  depotState: DepotState;
  productState: ProductState;
}) {
  const [isLoadingCurrInventory, setIsLoadingCurrInventory] = useState(true);
  const [currInventory, setCurrInventory] = useState<InventoryCsvRow[]>();
  const [, setProductDepotInfo] = useState<IFarmInputProductDepotInfo[]>();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);

  const dateFormatted = moment().format('YYYY-MM-DD');
  const currInventoryFileName = `${dateFormatted} Inventory - ${props.user.company?.name}.csv`;

  const depots = new PermissionsService(props.user).getEditableDepots(
    props.depotState.depots,
  );

  useEffect(() => {
    if (!props.open) return;
    setIsLoadingCurrInventory(true);
    const depotIds = depots.map((depot) => depot.id.toString());
    const productIds = props.productState.products.map((product) =>
      product.id.toString(),
    );
    apiGetProductDepotInfo({productIds, depotIds})
      .then((productDepotInfo) => {
        setProductDepotInfo(productDepotInfo);
        const template = generateInventoryCsv(
          depots,
          props.productState.products,
          productDepotInfo,
        );
        setCurrInventory(template);
      })
      .finally(() => setIsLoadingCurrInventory(false));
  }, [props.open]);

  const readCsv = (file: any): Promise<any[]> => {
    return new Promise<InventoryCsvRow[]>((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => resolve(results.data as any[]),
        error: (error) => reject(error),
      });
    });
  };

  const handleUpload = async (event: any) => {
    const document = event?.target?.files?.[0];
    if (!document) return;

    const mapper = new InventoryCsvMapper(depots, props.productState.products);
    const refreshInventory = async () => {
      const depotIds = depots.map((depot) => depot.id.toString());
      const productIds = props.productState.products.map((product) =>
        product.id.toString(),
      );
      const updates = await apiGetProductDepotInfo({
        productIds,
        depotIds,
      });
      reduxUpdateProductDepotInfo(updates);
    };

    setUploadErrors([]);
    setIsUploading(true);
    readCsv(document)
      .then((rows) => mapper.map(rows))
      .then((mapping) => {
        if (mapping.errors.length) {
          setUploadErrors(mapping.errors);
          return;
        }
        if (!mapping.input) return;
        return apiSaveProductDepotInfo(mapping.input)
          .then(refreshInventory)
          .then(() => {
            props.onSuccess();
            props.onClose();
          });
      })
      .catch((err) => {
        alert(
          'Failed to update inventory at the moment. Please try again later',
        );
        console.error(err);
      })
      .finally(() => setIsUploading(false));

    // todo should not work for AECI or khula admins
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      className="update-password-dialog"
    >
      <DialogTitle>Upload Inventory</DialogTitle>
      <DialogContent>
        {(isLoadingCurrInventory || isUploading) && (
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress size={20} thickness={6} />
          </Box>
        )}
        {!isLoadingCurrInventory && (
          <div className="generic-form-container form">
            {!!uploadErrors.length && (
              <Box
                marginBottom={2}
                maxHeight="15rem"
                style={{overflowY: 'scroll'}}
              >
                <Alert variant="outlined" severity="error" icon={false}>
                  <AlertTitle>Error Uploading CSV: </AlertTitle>
                  <ul style={{paddingLeft: '1rem'}}>
                    {uploadErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              </Box>
            )}
            <Box marginBottom={2}>
              <CSVLink filename={currInventoryFileName} data={currInventory!}>
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  fullWidth
                  disabled={isLoadingCurrInventory}
                >
                  Download Current
                </Button>
              </CSVLink>
            </Box>
            <Box>
              <Button
                variant="contained"
                size="small"
                color="primary"
                component="label" // important for input upload to work
                fullWidth
                disabled={isLoadingCurrInventory || isUploading}
              >
                <input
                  type="file"
                  accept="text/csv"
                  hidden
                  onChange={(event) => handleUpload(event)}
                />
                Upload CSV
              </Button>
            </Box>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    productState: state.products,
    productDepotInfo: state.productDepotInfos,
    subSubCategoriesState: state.subSubCategories,
    depotState: state.depots,
  };
};

export default connect(mapStateToProps)(InventoryCsvUploadDialog);
